/**
 * The social card — the image LinkedIn, X, Slack and WhatsApp show when
 * somebody pastes a link to this site.
 *
 * Fatih, 2026-09-04: *"ana sayfamızın og image ı yokmuş (mesela linkedinde
 * yorumlara digitopia.ai yazınca otomatik çıkan preview) onun için bir tasarım
 * yaptım"*. He was right, and it had been true of every page since launch:
 * `twitter:card` promised `summary_large_image` and no `og:image` was ever
 * emitted, so every share of digitopia.ai was a bare blue link. The imported
 * pages were missing `og:description` and the whole Twitter set as well.
 *
 * ---------------------------------------------------------------------------
 * WHY THE PNG AND NOT THE WEBP VARIANT NEXT TO IT.
 *
 * Fatih's artwork lives at `photo-src/digitopia-og-card.png`, and the photo
 * pipeline has already produced `public/photo/digitopia-og-card-1200.webp` from
 * it at the same 1200x630. That variant is the wrong file to name here, and the
 * reason is the platform that prompted the work: **LinkedIn documents PNG, JPEG
 * and GIF, and not WebP.** So does the rest of the long tail — Slack, WhatsApp
 * and Teams unfurl WebP unevenly. `optimize-images.mjs` emits WebP only, by
 * design and correctly, because everything else it touches is an `<img>` the
 * site's own CSS already gates on browser support. A scraper is not a browser
 * and does not read our CSS.
 *
 * Hence a second copy of the same artwork, kept OUT of the pipeline in
 * `public/og/`: 190 kB in the repo once, against the risk of the card not
 * rendering on the one surface Fatih was complaining about. `public/og/` and
 * not `photo-src/` is load-bearing — anything under `photo-src/` gets re-encoded
 * to WebP and resized, and this file must ship byte-exact at the declared size.
 *
 * ---------------------------------------------------------------------------
 * WHY THE DIMENSIONS ARE WRITTEN DOWN HERE AND CHECKED SOMEWHERE ELSE.
 *
 * This module is imported by CLIENT code (`app/Seo.tsx` -> `lib/seo.ts`), so it
 * cannot touch `node:fs` — the same trap `lib/site.ts` set on 2026-09-01, where
 * importing it into the Worker broke the build on `import.meta.env`. So the
 * numbers are constants, not measurements.
 *
 * A constant that describes a file is a two-sided contract, and D52 says those
 * get an assertion that runs on every build rather than a comment asking people
 * to keep them in step. `pnpm verify` reads the PNG's IHDR header and fails the
 * build if the file is present but is not a PNG or not exactly this size.
 *
 * MISSING reports rather than fails, and that asymmetry is deliberate. The meta
 * tags are emitted ONLY when the prerender finds the file, because a tag naming
 * a 404 is worse than no tag — the scraper caches the failure and LinkedIn's
 * cache is not ours to purge. `prerender.tsx` does that check (it is Node-only);
 * this module cannot, which is why the numbers are constants.
 *
 * Why WRONG fails while missing does not: a scraper lays the card out from these
 * numbers before it fetches the image, so a file re-exported at another size
 * renders wrong in a cache we cannot purge. Wrong-and-cached outlives the fix;
 * absent does not.
 *
 * 1200x630 is the size every platform documents for a large card. One card for
 * the whole site for now — per-page and per-locale cards are a real improvement
 * and a much bigger job (a generator, the brand font, a build step). This closes
 * "every share is a bare link", which is the part that was costing us.
 */
export interface OgCard {
  path: string
  mime: string
  width: number
  height: number
  /** Read aloud by screen readers, and shown by clients that cannot fetch it. */
  alt: string
}

export const OG_CARD: OgCard = {
  path: '/og/digitopia-card.png',
  mime: 'image/png',
  width: 1200,
  height: 630,
  alt: 'digitopia — Discover. Deploy. Outcome. #ScaleValue',
}
